import { useCallback, useMemo, useState } from "react";

import { useLocalization } from "@fluent/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";
import { useNotificationContext } from "cerulean";

import { useFilterContext } from "../context/FilterContext";

const MAX_SAVED_FILTER_NAME_LENGTH = 35;
const QUERY_KEY = "transactionSavedFilters";
const API_PATH = "transactions/saved_filters/";

/** Returns only the non-empty entries from filterState as a plain object. */
const collectActiveFilters = (
  filterState: Record<string, string>
): Record<string, string> =>
  Object.entries(filterState).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (value !== "") acc[key] = value;
      return acc;
    },
    {}
  );

export interface TransactionSavedFilter {
  id: string;
  name: string;
  filters: Record<string, string>;
}

interface TransactionSavedFilterListResponse {
  transaction_saved_filters: TransactionSavedFilter[];
}

export interface UseTransactionSavedFiltersResult {
  savedFilters: TransactionSavedFilter[];
  isLoading: boolean;
  selectedSavedFilter: TransactionSavedFilter | null;
  isModified: boolean;
  selectSavedFilter: (filterId: string) => void;
  clearSelection: () => void;
  createSavedFilter: (name: string) => Promise<boolean>;
  updateSavedFilter: () => Promise<void>;
  deleteSavedFilter: (filterId: string) => Promise<void>;
  validateName: (name: string) => string | null;
}

export const useTransactionSavedFilters = (
  enabled = true
): UseTransactionSavedFiltersResult => {
  const queryClient = useQueryClient();
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  const { filterState, updateFilters } = useFilterContext();

  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<
    string | null
  >(null);

  const { data: savedFilters = [], isLoading } = useQuery<
    TransactionSavedFilter[]
  >({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const response = await ApiHttp.fetch<TransactionSavedFilterListResponse>(
        API_PATH
      );
      return response.transaction_saved_filters ?? [];
    },
    enabled,
  });

  const selectedSavedFilter =
    savedFilters.find((v) => v.id === selectedSavedFilterId) ?? null;

  const isModified = useMemo(() => {
    if (!selectedSavedFilter) return false;
    const savedFilterEntries = selectedSavedFilter.filters;
    const activeFilters = collectActiveFilters(filterState);
    if (
      Object.keys(activeFilters).length !==
      Object.keys(savedFilterEntries).length
    )
      return true;
    return Object.keys(savedFilterEntries).some(
      (key) => activeFilters[key] !== savedFilterEntries[key]
    );
  }, [selectedSavedFilter, filterState]);

  const selectSavedFilter = useCallback(
    (filterId: string): void => {
      const savedFilter = savedFilters.find((v) => v.id === filterId);
      if (!savedFilter) return;
      setSelectedSavedFilterId(filterId);
      const allKeys = [
        ...Object.keys(filterState),
        ...Object.keys(savedFilter.filters),
      ];
      const updates = [...new Set(allKeys)].reduce<
        Record<string, string | undefined>
      >((acc, key) => {
        acc[key] = savedFilter.filters[key] ?? undefined;
        return acc;
      }, {});
      updateFilters(updates);
    },
    [savedFilters, filterState, updateFilters]
  );

  const clearSelection = useCallback(() => {
    setSelectedSavedFilterId(null);
  }, []);

  const createSavedFilter = useCallback(
    async (name: string): Promise<boolean> => {
      try {
        const filters = collectActiveFilters(filterState);
        await ApiHttp.fetch(API_PATH, { method: "POST" }, { name, filters });
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEY],
        });
        sendNotification({
          type: "success",
          text: l10n.getString("save-view-success"),
        });
        return true;
      } catch {
        sendNotification({
          type: "negative",
          text: l10n.getString("save-view-error"),
        });
        return false;
      }
    },
    [filterState, queryClient, sendNotification, l10n]
  );

  const updateSavedFilter = useCallback(async (): Promise<void> => {
    if (!selectedSavedFilter) return;
    try {
      const filters = collectActiveFilters(filterState);
      await ApiHttp.fetch(
        `${API_PATH}${selectedSavedFilter.id}/`,
        { method: "PUT" },
        { name: selectedSavedFilter.name, filters }
      );
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY],
      });
      sendNotification({
        type: "success",
        text: l10n.getString("update-view-success"),
      });
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("update-view-error"),
      });
    }
  }, [selectedSavedFilter, filterState, queryClient, sendNotification, l10n]);

  const deleteSavedFilter = useCallback(
    async (filterId: string): Promise<void> => {
      try {
        await ApiHttp.fetch(`${API_PATH}${filterId}/`, {
          method: "DELETE",
        });
        if (selectedSavedFilterId === filterId) {
          setSelectedSavedFilterId(null);
        }
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEY],
        });
        sendNotification({
          type: "success",
          text: l10n.getString("delete-view-success"),
        });
      } catch {
        sendNotification({
          type: "negative",
          text: l10n.getString("delete-view-error"),
        });
      }
    },
    [selectedSavedFilterId, queryClient, sendNotification, l10n]
  );

  const validateName = useCallback(
    (name: string): string | null => {
      const trimmed = name.trim();
      if (!trimmed) return l10n.getString("saved-filters-error-required");
      if (trimmed.length > MAX_SAVED_FILTER_NAME_LENGTH) {
        return l10n.getString("saved-filters-error-name-too-long", {
          max: MAX_SAVED_FILTER_NAME_LENGTH,
        });
      }
      const isDuplicate = savedFilters.some(
        (v) => v.name.toLowerCase() === trimmed.toLowerCase()
      );
      if (isDuplicate)
        return l10n.getString("saved-filters-error-name-duplicate");
      return null;
    },
    [savedFilters, l10n]
  );

  return {
    savedFilters,
    isLoading,
    selectedSavedFilter,
    isModified,
    selectSavedFilter,
    clearSelection,
    createSavedFilter,
    updateSavedFilter,
    deleteSavedFilter,
    validateName,
  };
};
